import * as SecureStore from 'expo-secure-store';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Platform } from 'react-native';

const WEB_STORAGE_PREFIX = 'coloursense_';

/** AsyncStorage keys that are user/session-specific - cleared on logout */
const USER_ASYNC_STORAGE_KEYS = [
  'session_id',
  'coloursense_subscription',
] as const;

/** Keys to keep when clearing on logout (e.g. tour completed so we don’t show tutorial again) */
const PRESERVE_KEYS_ON_LOGOUT = ['coloursense_tour_completed'];

export const saveToken = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.setItem(`${WEB_STORAGE_PREFIX}${key}`, value);
    return;
  }

  await SecureStore.setItemAsync(key, value);
};

export const getToken = async (key: string): Promise<string | null> => {
  try {
    if (Platform.OS === 'web') {
      return localStorage.getItem(`${WEB_STORAGE_PREFIX}${key}`);
    }

    return await SecureStore.getItemAsync(key);
  } catch {
    return null;
  }
};

export const removeToken = async (key: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.removeItem(`${WEB_STORAGE_PREFIX}${key}`);
    return;
  }

  await SecureStore.deleteItemAsync(key);
};

/** Pending onboarding data - saved when Complete Setup runs; submitted on sign-in if initial save failed */
const PENDING_ONBOARDING_KEY = 'pending_onboarding';

export const savePendingOnboarding = async (data: {
  name: string;
  age_bracket: string;
  industry: string;
}): Promise<void> => {
  await saveToken(PENDING_ONBOARDING_KEY, JSON.stringify(data));
};

export const getPendingOnboarding = async (): Promise<{
  name: string;
  age_bracket: string;
  industry: string;
} | null> => {
  try {
    const raw = await getToken(PENDING_ONBOARDING_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as {
      name: string;
      age_bracket: string;
      industry: string;
    };
  } catch {
    return null;
  }
};

export const clearPendingOnboarding = async (): Promise<void> => {
  await removeToken(PENDING_ONBOARDING_KEY);
};

const AUTH_KEYS = [
  'user_data',
  'auth_token',
  'refresh_token',
  'token_expires',
] as const;
const USER_STORAGE_KEY_PREFIXES = [
  'color_profiles_',
  'recent_analysis_cache_',
  'wardrobe_items_',
] as const;

/** Clear user/session-specific AsyncStorage (call on logout) */
const clearUserAsyncStorage = async (): Promise<void> => {
  try {
    await AsyncStorage.multiRemove([...USER_ASYNC_STORAGE_KEYS]);
  } catch {
    // Non-fatal - preferences like cookie_consent, theme, language are kept
  }
};

export const clearAllAppStorage = async (
  userId?: string | null
): Promise<void> => {
  if (Platform.OS === 'web') {
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith(WEB_STORAGE_PREFIX) &&
          !PRESERVE_KEYS_ON_LOGOUT.includes(key)
      )
      .forEach((key) => localStorage.removeItem(key));
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
    await clearUserAsyncStorage();
    return;
  }

  for (const key of AUTH_KEYS) {
    await SecureStore.deleteItemAsync(key);
  }
  if (userId) {
    for (const prefix of USER_STORAGE_KEY_PREFIXES) {
      await SecureStore.deleteItemAsync(`${prefix}${userId}`);
    }
  }
  await clearUserAsyncStorage();
};

export const clearStorage = async (): Promise<void> => {
  if (Platform.OS === 'web') {
    Object.keys(localStorage)
      .filter(
        (key) =>
          key.startsWith(WEB_STORAGE_PREFIX) &&
          !PRESERVE_KEYS_ON_LOGOUT.includes(key)
      )
      .forEach((key) => localStorage.removeItem(key));
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.clear();
    }
    await clearUserAsyncStorage();
    return;
  }
  for (const key of AUTH_KEYS) {
    await SecureStore.deleteItemAsync(key);
  }
  await clearUserAsyncStorage();
};
